import { el } from '../../dom';

/**
 * AFK / auto-exit (Block 10). Watches for input (mouse / keyboard / touch); after
 * `idleMs` of none, it soft-exits — but 30s before that it shows a "Still there?"
 * countdown with a Stay button. Pausing on exit is what stops energy draining and
 * keeps the 4-hour lockout timer from running while you're away (the farm ticker
 * is stopped by the caller's onExit).
 *
 * The guard only tracks time + input; the caller decides what "exit" and "resume"
 * mean (pause the ticker, save position, show the away screen).
 */
export interface AfkGuard {
  /** Call after resuming so the countdown starts fresh. */
  reset(): void;
  destroy(): void;
}

export function createAfkGuard(opts: {
  host: HTMLElement;
  idleMs: number;      // total idle before exit
  warnMs: number;      // warning window before exit (e.g. 30s)
  onExit: () => void;  // fired when the idle limit is reached
}): AfkGuard {
  const { host, idleMs, warnMs, onExit } = opts;
  let last = Date.now();
  let warned = false;
  let fired = false;   // latched after onExit so it fires once, until reset()
  let overlay: HTMLElement | null = null;
  let countdownEl: HTMLElement | null = null;

  const clearWarn = () => { warned = false; overlay?.remove(); overlay = null; countdownEl = null; };
  const bump = () => { last = Date.now(); if (warned) clearWarn(); };

  function showWarning() {
    warned = true;
    countdownEl = el('div', 'text-4xl font-heading text-white tabular-nums', {}, '0:30');
    overlay = el('div', 'absolute inset-0 z-30 flex flex-col items-center justify-center gap-4 bg-black/70 backdrop-blur-sm',
      {},
      el('div', 'text-lg text-white/90', {}, 'Still there?'),
      countdownEl,
      el('button', 'px-5 py-2 rounded-lg bg-purple/40 border border-purple/70 hover:border-purple text-white', { onClick: () => bump() }, "I'm here — stay"));
    host.appendChild(overlay);
  }

  const timer = window.setInterval(() => {
    if (fired) return;   // already away — wait for reset()
    const idle = Date.now() - last;
    if (idle >= idleMs) { fired = true; clearWarn(); onExit(); return; }
    if (idle >= idleMs - warnMs) {
      if (!warned) showWarning();
      const left = Math.max(0, Math.ceil((idleMs - idle) / 1000));
      if (countdownEl) countdownEl.textContent = `0:${String(left).padStart(2, '0')}`;
    }
  }, 1000);

  const events: (keyof WindowEventMap)[] = ['pointerdown', 'keydown', 'touchstart', 'wheel'];
  for (const e of events) window.addEventListener(e, bump, { passive: true });

  return {
    reset: () => { last = Date.now(); fired = false; clearWarn(); },
    destroy() {
      window.clearInterval(timer);
      for (const e of events) window.removeEventListener(e, bump);
      clearWarn();
    },
  };
}
